import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Mail, Phone, RefreshCw } from "lucide-react";
import { API } from "../Sidebar";

const REP_API = `${API}/reputation`;

const URGENCY_COLOR = {
  Low: "bg-slate-100 text-slate-600",
  Medium: "bg-blue-100 text-blue-700",
  High: "bg-orange-100 text-orange-700",
  Critical: "bg-rose-100 text-rose-700",
};
const STATUS_COLOR = {
  new: "bg-rose-100 text-rose-700",
  contacted: "bg-blue-100 text-blue-700",
  qualified: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-600",
};

export const ReputationLeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${REP_API}/leads?limit=200`);
      setLeads(data.items || []);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markStatus = async (leadId, status) => {
    try {
      await axios.patch(`${REP_API}/leads/${leadId}`, { status });
      toast.success(`Lead marked as ${status}`);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  return (
    <div className="space-y-6" data-testid="reputation-leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: "var(--font-heading)" }}>
            Reputation — Leads
          </h1>
          <p className="text-slate-500 mt-1">
            Free Audit submissions from FakeReviewRemovalPro.com
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-leads-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          </div>
        ) : leads.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="empty-leads">
            No leads yet. Free Audit forms will appear here once the site is live.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business</TableHead>
                <TableHead>Industry</TableHead>
                <TableHead># Reviews</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Urgency</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((l) => (
                <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                  <TableCell>
                    <div className="font-medium">{l.business_name}</div>
                    <a href={l.gmb_url} target="_blank" rel="noopener" className="text-xs text-blue-600 underline">GMB Profile →</a>
                  </TableCell>
                  <TableCell className="text-sm">{l.industry || "—"}</TableCell>
                  <TableCell className="text-center font-semibold">{l.number_of_fake_reviews}</TableCell>
                  <TableCell className="text-sm">
                    <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {l.email}</div>
                    <div className="flex items-center gap-1 text-slate-500"><Phone className="w-3 h-3" /> {l.phone}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={URGENCY_COLOR[l.urgency] || URGENCY_COLOR.Medium}>{l.urgency}</Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_COLOR[l.status] || STATUS_COLOR.new}>{l.status}</Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">
                    {new Date(l.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => markStatus(l.id, "contacted")} data-testid={`mark-contacted-${l.id}`}>Contact</Button>
                      <Button size="sm" variant="outline" onClick={() => markStatus(l.id, "qualified")} data-testid={`mark-qualified-${l.id}`}>Qualify</Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};

export default ReputationLeadsPage;
